import React, { Component } from 'react';
import { Client, Nullable, Therapist, UserType, SpeechNeed } from '../interfaces/Frameworks';
import { Theme } from '../Theme';
import { Button } from './Button';
import { CheckboxPullDownMenu, CheckboxPullDownMenuOption } from './CheckboxPullDownMenu';
import { FunnelWidget } from './FunnelWidget';
import { Sparkline } from './Sparkline';
import { getUserTypeLabel, UserTypeTag } from './UserTypeTag';
import { getClientSpeechNeedNames, getClientSpeechNeeds } from '../clientSpeechNeeds';
import { downloadClientsImportTemplate } from './ClientsImportTemplate';
import { TableRowEllipsisMenuButton } from './TableRowEllipsisMenuButton';

export type ClientsListSortKey = 'name' | 'type' | 'dateOfBirth' | 'speechNeed' | 'commsCampaign' | 'carers' | 'therapist' | 'notes' | 'activity' | 'active' | 'lastSignInTime' | 'friendlyId' | 'id';

export type ClientsListPersistentState = {
    filterText: string;
    selectedSupportAreaId: string;
    selectedUserTypes: Array<UserTypeFilterId>;
    sortDirection: 'asc' | 'desc';
    sortKey: ClientsListSortKey;
};

const USER_TYPE_ALL_FILTER_ID = 'all';
type UserTypeFilterId = typeof USER_TYPE_ALL_FILTER_ID | UserType;
const INDIVIDUAL_USER_TYPE_FILTER_OPTIONS: Array<CheckboxPullDownMenuOption & { id: UserType }> = [
    { id: 'carer', label: 'Carer' },
    { id: 'care_recipient', label: 'Care recipient' },
    { id: 'therapist', label: 'Therapist' },
    { id: 'admin', label: 'Admin' },
    { id: 'super', label: 'Super' },
];
const USER_TYPE_FILTER_OPTIONS: Array<CheckboxPullDownMenuOption & { id: UserTypeFilterId }> = [
    { id: USER_TYPE_ALL_FILTER_ID, label: 'ALL' },
    ...INDIVIDUAL_USER_TYPE_FILTER_OPTIONS,
];

export const DEFAULT_CLIENTS_LIST_STATE: ClientsListPersistentState = {
    filterText: '',
    selectedSupportAreaId: '',
    selectedUserTypes: [USER_TYPE_ALL_FILTER_ID],
    sortDirection: 'asc',
    sortKey: 'name',
};

interface ClientsListProps {
    authenticatedUserType: UserType;
    clients: Array<Client>;
    activityByClientId: Record<string, Array<number>>;
    stageByClientId: Record<string, string>;
    therapists: Array<Therapist>;
    currentTherapist: Nullable<Therapist>;
    speechNeeds: Array<SpeechNeed>;
    healthcareProviderName: string;
    healthcareProviderLogoUrl?: string;
    onAddClient: () => void;
    onImport: (file: File) => void | Promise<void>;
    importButtonLabel?: string;
    isImporting?: boolean;
    onSelectClient: (client: Client) => void;
    selectedClient: Nullable<Client>;
    listState: ClientsListPersistentState;
    onListStateChange: (listState: ClientsListPersistentState) => void;
}

interface ClientsListState extends ClientsListPersistentState {
    focusedClient: Nullable<Client>;
    isDownloadingTemplate: boolean;
    templateError: string | null;
}

class ClientsList extends Component<ClientsListProps, ClientsListState> {
    private fileInputRef = React.createRef<HTMLInputElement>();
    private readonly stageOrder: Record<string, number> = {
        referral_created_not_signed_in: 0,
        signed_in_successfully: 1,
        completed_onboarding: 2,
        first_used_app: 3,
    };

    constructor(props: ClientsListProps) {
        super(props);
        const initialListState = this.getNormalizedPersistentState({
            ...DEFAULT_CLIENTS_LIST_STATE,
            ...props.listState,
        });
        this.state = {
            ...initialListState,
            focusedClient: null,
            isDownloadingTemplate: false,
            templateError: null,
        };
    }

    public componentDidMount(): void {
        this.clearUnavailableSupportAreaFilter();
    }

    public componentDidUpdate(prevProps: ClientsListProps, prevState: ClientsListState): void {
        // Keep the remounted list aligned if its parent restores saved filter/sort state.
        const nextListState = this.getNormalizedPersistentState(this.props.listState);
        if (this.hasDifferentPersistentState(this.getNormalizedPersistentState(prevProps.listState), nextListState) &&
            this.hasDifferentPersistentState(this.getPersistentState(), nextListState)
        ) {
            this.setState({ ...nextListState });
            return;
        }

        if (
            prevState.selectedSupportAreaId === this.state.selectedSupportAreaId &&
            prevProps.clients === this.props.clients &&
            prevProps.speechNeeds === this.props.speechNeeds
        ) {
            return;
        }

        this.clearUnavailableSupportAreaFilter();
    }

    private getPersistentState(state: ClientsListState = this.state): ClientsListPersistentState {
        // Persist only list controls; the transient highlighted row stays local.
        return {
            filterText: state.filterText,
            selectedSupportAreaId: state.selectedSupportAreaId,
            selectedUserTypes: state.selectedUserTypes,
            sortDirection: state.sortDirection,
            sortKey: state.sortKey,
        };
    }

    private getNormalizedPersistentState(listState: ClientsListPersistentState): ClientsListPersistentState {
        return {
            ...listState,
            // Older saved dashboard state sorted this column by raw document id.
            sortKey: listState.sortKey === 'id' ? 'friendlyId' : listState.sortKey,
        };
    }

    private hasDifferentPersistentState(
        left: ClientsListPersistentState,
        right: ClientsListPersistentState
    ): boolean {
        return left.filterText !== right.filterText ||
            left.selectedSupportAreaId !== right.selectedSupportAreaId ||
            left.selectedUserTypes.join('|') !== right.selectedUserTypes.join('|') ||
            left.sortDirection !== right.sortDirection ||
            left.sortKey !== right.sortKey;
    }

    private notifyPersistentStateChange() {
        // Push filter and sort changes up before the list can be unmounted.
        this.props.onListStateChange(this.getPersistentState());
    }

    private clearUnavailableSupportAreaFilter() {
        const { selectedSupportAreaId } = this.state;

        // Reset the filter when the current client list no longer contains that support area.
        if (
            selectedSupportAreaId &&
            !this.getAvailableSupportAreaOptions().some((option) => option.id === selectedSupportAreaId)
        ) {
            this.setState({ selectedSupportAreaId: '' }, () => this.notifyPersistentStateChange());
        }
    }

    private formatClientName(client: Client): string {
        if (client.givenName && client.familyName) {
            return `${client.familyName}, ${client.givenName}`;
        }
        return client.givenName || client.displayName || '';
    }

    // Export an empty workbook using this provider's current branding and available support areas.
    private handleDownloadTemplate = async () => {
        this.setState({ isDownloadingTemplate: true, templateError: null });
        try {
            await downloadClientsImportTemplate(this.props.speechNeeds, {
                name: this.props.healthcareProviderName,
                logoUrl: this.props.healthcareProviderLogoUrl,
            });
        } catch (error) {
            this.setState({ templateError: 'The import template could not be downloaded. Please try again.' });
        } finally {
            this.setState({ isDownloadingTemplate: false });
        }
    };

    private getSpeechNeedName(client: Client): string {
        return getClientSpeechNeedNames(client, this.props.speechNeeds);
    }

    private getSupportAreaLabel(speechNeedId: string): string {
        return this.props.speechNeeds.find((speechNeed) => speechNeed.id === speechNeedId)?.name || speechNeedId;
    }

    private getAvailableSupportAreaOptions(): Array<{ id: string; label: string }> {
        // Build the filter options from the support areas present in the current client list.
        return Array.from(
            new Set(
                this.props.clients
                    .flatMap((client) => getClientSpeechNeeds(client))
            )
        )
            .map((speechNeedId) => ({
                id: speechNeedId,
                label: this.getSupportAreaLabel(speechNeedId),
            }))
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    }

    private getTherapistName(client: Client): string {
        const currentTherapistName =
            this.props.currentTherapist && this.props.currentTherapist.id === client.therapistUserId
                ? this.props.currentTherapist.name
                : '';

        return this.props.therapists.find(therapist => therapist.id === client.therapistUserId)?.name ||
            currentTherapistName ||
            '';
    }

    private getComparableDateValue(value?: string): number {
        if (!value) {
            return Number.POSITIVE_INFINITY;
        }
        const date = new Date(value);
        return isNaN(date.getTime()) ? Number.POSITIVE_INFINITY : date.getTime();
    }

    private getCommsCampaignLabel(client: Client): string {
        return client.commsCampaignId ? 'YES' : '--';
    }

    private shouldShowEngagementIndicators(client: Client): boolean {
        const userType = (client.userType || '').toLowerCase();
        const isCareRecipient = userType === 'care_recipient' || userType === 'client';
        const hasAppContactMethod = !!client.phoneNumber || !!client.email;

        return hasAppContactMethod && !isCareRecipient;
    }

    private getNormalizedUserType(client: Client): UserType | null {
        // Treat the legacy "client" type as a care recipient for filtering.
        const rawUserType = client.userType as string;
        if (rawUserType === 'client') {
            return 'care_recipient';
        }

        return INDIVIDUAL_USER_TYPE_FILTER_OPTIONS.some((option) => option.id === rawUserType) ?
            rawUserType as UserType :
            null;
    }

    private isAllUserTypeFilterSelected(selectedUserTypes: Array<UserTypeFilterId>): boolean {
        // The ALL option intentionally includes users whose userType is missing or unknown.
        return selectedUserTypes.includes(USER_TYPE_ALL_FILTER_ID) ||
            INDIVIDUAL_USER_TYPE_FILTER_OPTIONS.every((option) => selectedUserTypes.includes(option.id));
    }

    private getUserTypeFilterSummary(): string {
        const { selectedUserTypes } = this.state;
        if (this.isAllUserTypeFilterSelected(selectedUserTypes)) {
            return 'All user types';
        }

        if (selectedUserTypes.length === 0) {
            return 'No user types';
        }

        if (selectedUserTypes.length === 1) {
            return INDIVIDUAL_USER_TYPE_FILTER_OPTIONS.find((option) => option.id === selectedUserTypes[0])?.label || 'User type';
        }

        return `${selectedUserTypes.length} user types`;
    }

    private handleUserTypesChange = (selectedUserTypes: Array<string>) => {
        // Persist user-type filters in the same lifted state as search and sorting.
        const previousHadAll = this.state.selectedUserTypes.includes(USER_TYPE_ALL_FILTER_ID);
        const nextHasAll = selectedUserTypes.includes(USER_TYPE_ALL_FILTER_ID);
        const selectedKnownUserTypes = selectedUserTypes.filter((userType): userType is UserType =>
            INDIVIDUAL_USER_TYPE_FILTER_OPTIONS.some((option) => option.id === userType)
        );
        let nextSelectedUserTypes: Array<UserTypeFilterId> = selectedKnownUserTypes;

        if (nextHasAll && !previousHadAll) {
            nextSelectedUserTypes = [USER_TYPE_ALL_FILTER_ID];
        } else if (nextHasAll && selectedKnownUserTypes.length > 0) {
            nextSelectedUserTypes = selectedKnownUserTypes;
        } else if (selectedKnownUserTypes.length === INDIVIDUAL_USER_TYPE_FILTER_OPTIONS.length) {
            nextSelectedUserTypes = [USER_TYPE_ALL_FILTER_ID];
        }

        this.setState({ selectedUserTypes: nextSelectedUserTypes }, () => this.notifyPersistentStateChange());
    }

    private getSortValue(client: Client, sortKey: ClientsListSortKey): string | number {
        switch (sortKey) {
            case 'name':
                return this.formatClientName(client).toLowerCase();
            case 'type':
                return getUserTypeLabel(client.userType).toLowerCase();
            case 'dateOfBirth':
                return this.getComparableDateValue(client.dateOfBirth);
            case 'speechNeed':
                return this.getSpeechNeedName(client).toLowerCase();
            case 'commsCampaign':
                return this.getCommsCampaignLabel(client);
            case 'carers':
                return this.getCarerNames(client.carers).toLowerCase();
            case 'therapist':
                return this.getTherapistName(client).toLowerCase();
            case 'notes':
                return (client.notes || '').toLowerCase();
            case 'activity':
                if (!this.shouldShowEngagementIndicators(client)) {
                    return -1;
                }
                return (this.props.activityByClientId[client.id || ''] || []).reduce((sum, value) => sum + value, 0);
            case 'active':
                if (!this.shouldShowEngagementIndicators(client)) {
                    return -1;
                }
                return this.stageOrder[this.props.stageByClientId[client.id || ''] || 'referral_created_not_signed_in'] || 0;
            case 'lastSignInTime':
                return this.getComparableDateValue(client.lastSignInTime);
            case 'friendlyId':
            case 'id':
                return (client.friendlyId || '').toLowerCase();
            default:
                return '';
        }
    }

    private compareClients(a: Client, b: Client): number {
        const { sortDirection, sortKey } = this.state;
        const aValue = this.getSortValue(a, sortKey);
        const bValue = this.getSortValue(b, sortKey);

        let comparison = 0;
        if (typeof aValue === 'number' && typeof bValue === 'number') {
            comparison = aValue - bValue;
        } else {
            comparison = String(aValue).localeCompare(String(bValue), undefined, { sensitivity: 'base' });
        }

        if (comparison === 0) {
            comparison = this.formatClientName(a).localeCompare(this.formatClientName(b), undefined, { sensitivity: 'base' });
        }

        return sortDirection === 'asc' ? comparison : -comparison;
    }

    private handleSort = (sortKey: ClientsListSortKey) => {
        this.setState((currentState) => ({
            sortKey,
            sortDirection: currentState.sortKey === sortKey && currentState.sortDirection === 'asc' ? 'desc' : 'asc',
        }), () => this.notifyPersistentStateChange());
    }

    private renderHeaderCell(
        label: string,
        sortKey: ClientsListSortKey,
        centered: boolean = false,
        width?: string
    ) {
        const isActiveSort = this.state.sortKey === sortKey;
        const baseStyle = centered ? styles.headerCellCenteredClickable : styles.headerCellClickable;
        const chevronStyle = this.state.sortDirection === 'asc' ? styles.sortChevronUp : styles.sortChevronDown;

        return (
            <th
                style={width ? { ...baseStyle, minWidth: width } : baseStyle}
                onClick={() => this.handleSort(sortKey)}
            >
                <div style={styles.headerCellContent}>
                    <span>{label}</span>
                    <span style={styles.sortChevronContainer}>
                        {isActiveSort ? <span style={chevronStyle}></span> : <span style={styles.sortChevronPlaceholder}></span>}
                    </span>
                </div>
            </th>
        );
    }

    private formatLastSignIn(lastSignInTime?: string): string {
        if (!lastSignInTime) return '';
        const date = new Date(lastSignInTime);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleDateString();
    }

    private getCarerNames(carerIds?: Array<string>): string {
        if (!carerIds || carerIds.length === 0) return '';
        
        const carerNames = carerIds.map(carerId => {
            const carer = this.props.clients.find(client => client.id === carerId);
            return carer ? this.formatClientName(carer) : '';
        }).filter(name => name);
        
        return carerNames.join(', ');
    }

    private renderClient(client: Client, idx: number) {
        const { onSelectClient } = this.props;
        const { focusedClient } = this.state;
        const isClientFocused = focusedClient?.id && focusedClient?.id === client.id;
        const therapistName = this.getTherapistName(client);
        
        return (
            <tr
                key={idx}
                onMouseDown={() => {this.setState({ focusedClient: client });}}
                onClick={() => onSelectClient(client)}
                style={{
                    cursor: 'pointer',
                    backgroundColor: isClientFocused ? '#817ee460' : 'transparent',
                }}
                role='button'
            >
                <td style={styles.tableCell}>{this.formatClientName(client)}</td>
                <td style={styles.tableCell}><UserTypeTag userType={client.userType} /></td>
                <td style={styles.tableCell}>{client.dateOfBirth || ''}</td>
                <td style={styles.tableCell}>{this.getSpeechNeedName(client)}</td>
                <td style={styles.activeFunnelCell}>
                    {this.shouldShowEngagementIndicators(client) && (
                        <FunnelWidget
                            currentStage={this.props.stageByClientId[client.id || ''] || 'referral_created_not_signed_in'}
                            width={120}
                            height={16}
                            showLabels={false}
                        />
                    )}
                </td>
                <td style={styles.tableCellCentered}>{this.getCommsCampaignLabel(client)}</td>
                <td style={styles.tableCell}>{this.getCarerNames(client.carers)}</td>
                <td style={styles.tableCell}>{therapistName}</td>
                <td style={styles.tableCell}>{client.notes || ''}</td>
                <td style={styles.activityTableCell}>
                    {this.shouldShowEngagementIndicators(client) && (
                        <Sparkline values={this.props.activityByClientId[client.id || ''] || Array(14).fill(0)} width={96} maxValue={10} />
                    )}
                </td>
                <td style={styles.tableCell}>{this.formatLastSignIn(client.lastSignInTime)}</td>
                <td style={styles.tableCell}>{client.friendlyId || ''}</td>
            </tr>
        );
    }

    private renderClientsList() {
        const { clients } = this.props;
        const { filterText, selectedSupportAreaId, selectedUserTypes } = this.state;
        const selectedUserTypeSet = new Set<UserType>(
            selectedUserTypes.filter((userType): userType is UserType => userType !== USER_TYPE_ALL_FILTER_ID)
        );
        const isAllUserTypesSelected = this.isAllUserTypeFilterSelected(selectedUserTypes);
        const normalizedFilterText = filterText.toLowerCase();
        const sortedAndFilteredClients = [...clients]
            .filter((client) => {
                // Apply search, user type, and support-area filters before sorting the visible rows.
                const matchesSearch = [
                    this.formatClientName(client),
                    client.friendlyId,
                    client.phoneNumber,
                    client.email,
                    client.dateOfBirth,
                    // Search both the authentication user ID and the ID shown in client details.
                    client.userId,
                    client.id,
                ].some((value) => (value || '').toLowerCase().includes(normalizedFilterText));
                const matchesSupportArea = !selectedSupportAreaId || getClientSpeechNeeds(client).includes(selectedSupportAreaId);
                const userType = this.getNormalizedUserType(client);
                const matchesUserType = isAllUserTypesSelected || (!!userType && selectedUserTypeSet.has(userType));

                return matchesSearch && matchesSupportArea && matchesUserType;
            })
            .sort((a, b) => this.compareClients(a, b));
        
        return (
            <div style={styles.clientsList}>
                <table style={styles.table}>
                    <thead>
                        <tr style={styles.headerRow}>
                            {this.renderHeaderCell('Name', 'name', false, '180px')}
                            {this.renderHeaderCell('Type', 'type')}
                            {this.renderHeaderCell('Date of birth', 'dateOfBirth', false, '140px')}
                            {this.renderHeaderCell('Support areas', 'speechNeed')}
                            {this.renderHeaderCell('Active', 'active', true, '136px')}
                            {this.renderHeaderCell('Comms campaign', 'commsCampaign', true, '140px')}
                            {this.renderHeaderCell('Carers', 'carers')}
                            {this.renderHeaderCell('Therapist', 'therapist')}
                            {this.renderHeaderCell('Notes', 'notes')}
                            {this.renderHeaderCell('Activity', 'activity', false, '104px')}
                            {this.renderHeaderCell('Last signed in', 'lastSignInTime')}
                            {this.renderHeaderCell('Friendly ID', 'friendlyId', false, '140px')}
                        </tr>
                    </thead>
                    <tbody>
                        {sortedAndFilteredClients.map((client, idx) => (
                            this.renderClient(client, idx)
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    public render(): React.ReactNode {
        const { onAddClient, importButtonLabel, isImporting } = this.props;
        const { filterText, selectedSupportAreaId, selectedUserTypes } = this.state;
        const supportAreaOptions = this.getAvailableSupportAreaOptions();
        const visibleSelectedUserTypes = this.isAllUserTypeFilterSelected(selectedUserTypes) ?
            [USER_TYPE_ALL_FILTER_ID] :
            selectedUserTypes;

        return (
            <div style={styles.container}>

                {/* search box */}
                <div style={styles.searchContainer}>
                    <input
                        type='text'
                        value={filterText}
                        onChange={(e) => this.setState({ filterText: e.target.value }, () => this.notifyPersistentStateChange())}
                        placeholder='Search...'
                        style={styles.searchInput}
                    />
                    <CheckboxPullDownMenu
                        label='User type'
                        ariaLabel='Filter by user type'
                        options={USER_TYPE_FILTER_OPTIONS}
                        selectedIds={visibleSelectedUserTypes}
                        onChange={this.handleUserTypesChange}
                        summary={this.getUserTypeFilterSummary()}
                    />
                    <select
                        aria-label='Filter by support area'
                        value={selectedSupportAreaId}
                        onChange={(e) => this.setState({ selectedSupportAreaId: e.target.value }, () => this.notifyPersistentStateChange())}
                        style={styles.supportAreaSelect}
                    >
                        <option value=''>All support areas</option>
                        {supportAreaOptions.map((supportArea) => (
                            <option key={supportArea.id} value={supportArea.id}>
                                {supportArea.label}
                            </option>
                        ))}
                    </select>
                    <div style={styles.actionsContainer}>
                        <input
                            ref={this.fileInputRef}
                            type='file'
                            accept='.csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                            style={styles.hiddenInput}
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                    void this.props.onImport(file);
                                }
                                e.target.value = '';
                            }}
                        />
                        <Button onClick={onAddClient} label='New' icon={require('./assets/icons/add_selected.svg').default} type='primary' disabled={isImporting} />
                        {/* Keep spreadsheet actions in the shared menu beside New. */}
                        <TableRowEllipsisMenuButton
                            ariaLabel='Client actions'
                            options={[{
                                label: this.state.isDownloadingTemplate ? 'Preparing template...' : 'Download template',
                                onClick: this.handleDownloadTemplate,
                                disabled: isImporting || this.state.isDownloadingTemplate,
                            }, {
                                label: importButtonLabel || 'Import',
                                onClick: () => this.fileInputRef.current?.click(),
                                disabled: isImporting,
                            }]}
                        />
                    </div>
                </div>

                {this.state.templateError && (
                    <p role='alert' style={styles.templateError}>{this.state.templateError}</p>
                )}
                
                {/* clients list */}
                {this.renderClientsList()}
                
            </div>
        );
    }
}

const styles = {
    container: { 
        width: '100%', 
        background: Theme.Colours.background, 
        padding: '36px',
        boxSizing: 'border-box' as const,
        display: 'flex', 
        flexDirection: 'column' as const, 
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        height: '100%',
        overflow: 'hidden' as const,
    },
    buttonsContainer: { 
        display: 'flex', 
        margin: '0 0 16px 0', // top right bottom left
    },
    searchContainer: { 
        display: 'flex', 
        flexWrap: 'wrap' as const,
        alignItems: 'center',
        margin: '0 0 16px 0', // top right bottom left
        gap: '12px',
    },
    actionsContainer: {
        display: 'flex',
        flexShrink: 0,
        alignItems: 'center',
        gap: '12px',
    },
    templateError: {
        ...Theme.Fonts.body,
        color: '#b22d42',
        margin: '0 0 16px',
    },
    button: { 
        paddingRight: 14, 
        fontSize: 18,
        borderRadius: 25,
    },
    clientsList: {
        flexGrow: 1,
        minHeight: 0,
        minWidth: 0,
        overflowY: 'auto' as const,
        overflowX: 'auto' as const,
        paddingBottom: '20px',
    },
    table: {
        width: '100%',
        backgroundColor: Theme.Colours.workArea,
        borderRadius: '8px',
        overflow: 'hidden',
        border: Theme.Colours.border,
    },
    headerRow: {
        backgroundColor: Theme.Colours.tableHeader,
        color: Theme.Colours.text,
    },
    headerCell: {
        ...Theme.Fonts.body,
        fontWeight: 'bold' as const,
        padding: '16px 16px 16px 16px', // top right bottom left
        textAlign: 'left' as const,
        borderBottom: Theme.Colours.border,
    },
    tableCell: {
        ...Theme.Fonts.body,
        padding: '16px 16px 16px 16px', // top right bottom left
        borderBottom: Theme.Colours.border,
    },
    headerCellCentered: {
        ...Theme.Fonts.body,
        fontWeight: 'bold' as const,
        padding: '16px 16px 16px 16px',
        textAlign: 'center' as const,
        borderBottom: Theme.Colours.border,
    },
    headerCellClickable: {
        ...Theme.Fonts.body,
        fontWeight: 'bold' as const,
        padding: '16px 16px 16px 16px',
        textAlign: 'left' as const,
        borderBottom: Theme.Colours.border,
        cursor: 'pointer',
        userSelect: 'none' as const,
    },
    headerCellCenteredClickable: {
        ...Theme.Fonts.body,
        fontWeight: 'bold' as const,
        padding: '16px 16px 16px 16px',
        textAlign: 'center' as const,
        borderBottom: Theme.Colours.border,
        cursor: 'pointer',
        userSelect: 'none' as const,
    },
    headerCellContent: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        width: '100%',
    },
    sortChevronContainer: {
        width: '12px',
        display: 'flex',
        justifyContent: 'flex-end',
        flexShrink: 0,
    },
    sortChevronUp: {
        width: '7px',
        height: '7px',
        borderTop: '2px solid currentColor',
        borderRight: '2px solid currentColor',
        transform: 'rotate(-45deg)',
        marginTop: '3px',
        boxSizing: 'border-box' as const,
    },
    sortChevronDown: {
        width: '7px',
        height: '7px',
        borderBottom: '2px solid currentColor',
        borderRight: '2px solid currentColor',
        transform: 'rotate(45deg)',
        marginBottom: '3px',
        boxSizing: 'border-box' as const,
    },
    sortChevronPlaceholder: {
        width: '7px',
        height: '7px',
    },
    tableCellCentered: {
        ...Theme.Fonts.body,
        padding: '16px 16px 16px 16px',
        borderBottom: Theme.Colours.border,
        textAlign: 'center' as const,
    },
    activeFunnelCell: {
        ...Theme.Fonts.body,
        padding: '12px 8px',
        borderBottom: Theme.Colours.border,
        textAlign: 'center' as const,
        minWidth: '136px',
    },
    activityTableCell: {
        ...Theme.Fonts.body,
        padding: '12px 8px',
        borderBottom: Theme.Colours.border,
        minWidth: '104px',
    },
    searchInput: {
        flex: 1,
        padding: '12px 16px',
        margin: '0 0 0 0', // top right bottom left
        borderRadius: '8px',
        border: Theme.Colours.border,
    },
    supportAreaSelect: {
        ...Theme.Fonts.body,
        width: '240px',
        maxWidth: '100%',
        flexShrink: 0,
        padding: '12px 16px',
        borderRadius: '8px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
    },
    hiddenInput: {
        display: 'none',
    },
};

export default ClientsList;
